import { z } from "zod";

export const DELIVERY_RULE_SCOPE_VALUES = [
  "STREET",
  "DISTRICT",
  "POSTAL_CODE_CITY",
  "CITY",
] as const;

export const DeliverySuggestionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
});

export const DeliverySuggestionsSchema = z.object({
  suggestions: z.array(DeliverySuggestionSchema),
});

export type DeliverySuggestions = z.infer<typeof DeliverySuggestionsSchema>;

export const DeliveryQuoteAddressSchema = z.object({
  formattedLine: z.string().min(1),
  countryCode: z.string().min(1),
  postalCode: z.string(),
  city: z.string(),
  district: z.string().nullable(),
  street: z.string(),
  houseNumber: z.string(),
  complete: z.boolean(),
});

export const DeliveryQuoteSchema = z.object({
  address: DeliveryQuoteAddressSchema,
  deliverable: z.boolean(),
  belowMinimum: z.boolean(),
  minOrderCents: z.number().int().min(0).nullable(),
  deliveryFeeCents: z.number().int().min(0).nullable(),
  /** Free-delivery threshold after inheritance. null means no threshold. */
  freeDeliverySubtotalCents: z.number().int().min(0).nullable(),
  subtotalCents: z.number().int().min(0),
  totalCents: z.number().int().min(0).nullable(),
});

export type DeliveryQuote = z.infer<typeof DeliveryQuoteSchema>;

export const DeliveryQuoteBodySchema = z.object({
  providerId: z.string().trim().min(1).max(512),
  sessionToken: z.string().uuid(),
  restaurantId: z.string().optional(),
});

export type DeliveryQuoteBody = z.infer<typeof DeliveryQuoteBodySchema>;

const deliveryRuleMoney = {
  minOrderCents: z.number().int().min(0),
  deliveryFeeCents: z.number().int().min(0),
  /** Omitted or null inherits the threshold from the next larger matching rule. */
  freeDeliverySubtotalCents: z.number().int().min(0).nullable().optional(),
  /** True stops inheritance: no free-delivery threshold for this rule. */
  freeDeliveryNone: z.boolean().optional(),
  active: z.boolean(),
};

const noThresholdWithNone = (value: {
  freeDeliverySubtotalCents?: number | null | undefined;
  freeDeliveryNone?: boolean | undefined;
}) => !(value.freeDeliveryNone && (value.freeDeliverySubtotalCents ?? null) !== null);

export const ReplaceDeliveryRulesBodySchema = z.object({
  rules: z
    .array(
      z.union([
        z
          .object({
            id: z.string().uuid(),
            ...deliveryRuleMoney,
          })
          .refine(noThresholdWithNone, { message: "freeDeliveryNone excludes a threshold" }),
        z
          .object({
            providerId: z.string().trim().min(1).max(512),
            sessionToken: z.string().uuid(),
            scope: z.enum(DELIVERY_RULE_SCOPE_VALUES),
            ...deliveryRuleMoney,
          })
          .refine(noThresholdWithNone, { message: "freeDeliveryNone excludes a threshold" }),
      ]),
    )
    .max(100),
});

export type ReplaceDeliveryRulesBody = z.infer<typeof ReplaceDeliveryRulesBodySchema>;

export const DashboardDeliveryRuleSchema = z.object({
  id: z.string().uuid(),
  scope: z.enum(DELIVERY_RULE_SCOPE_VALUES),
  countryCode: z.literal("DE"),
  city: z.string().min(1),
  postalCode: z.string().nullable(),
  district: z.string().nullable(),
  street: z.string().nullable(),
  minOrderCents: z.number().int().min(0),
  deliveryFeeCents: z.number().int().min(0),
  freeDeliverySubtotalCents: z.number().int().min(0).nullable(),
  freeDeliveryNone: z.boolean(),
  minOrderBasis: z.literal("SUBTOTAL"),
  active: z.boolean(),
});

export const DashboardDeliveryRulesSchema = z.object({
  restaurantId: z.string().uuid(),
  rules: z.array(DashboardDeliveryRuleSchema),
});

export type DashboardDeliveryRule = z.infer<typeof DashboardDeliveryRuleSchema>;
export type DashboardDeliveryRules = z.infer<typeof DashboardDeliveryRulesSchema>;

export const StorefrontOrderAddressSchema = z.object({
  formattedLine: z.string().min(1),
  countryCode: z.literal("DE"),
  postalCode: z.string().min(1),
  city: z.string().min(1),
  district: z.string().nullable(),
  street: z.string().min(1),
  houseNumber: z.string().min(1),
});

export type StorefrontOrderAddress = z.infer<typeof StorefrontOrderAddressSchema>;

export const StorefrontDeliveryZoneSchema = z.object({
  id: z.string().uuid(),
  scope: z.enum(DELIVERY_RULE_SCOPE_VALUES),
  label: z.string().min(1),
  minOrderCents: z.number().int().min(0),
  deliveryFeeCents: z.number().int().min(0),
  /** Free-delivery threshold after inheritance from the city rule. null means none. */
  freeDeliverySubtotalCents: z.number().int().min(0).nullable(),
});

export const StorefrontDeliveryZonesSchema = z.object({
  restaurantId: z.string().uuid(),
  zones: z.array(StorefrontDeliveryZoneSchema),
});

export type StorefrontDeliveryZone = z.infer<typeof StorefrontDeliveryZoneSchema>;
export type StorefrontDeliveryZones = z.infer<typeof StorefrontDeliveryZonesSchema>;
